import React, { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'

const DEFAULT_HABITS = [
  { key: 'worked_out',        label: 'Worked out',            icon: '🏋️', area: 'health' },
  { key: 'read_bible',        label: 'Read Bible / journaled', icon: '📖', area: 'faith' },
  { key: 'reviewed_finances', label: 'Reviewed finances',      icon: '💸', area: 'career' },
  { key: 'meditated',         label: 'Meditated / prayed',     icon: '🧘', area: 'mind' },
  { key: 'reached_out',       label: 'Reached out to someone', icon: '📞', area: 'relationships' },
  { key: 'worked_on_goal',    label: 'Worked on main goal',    icon: '🎯', area: 'purpose' },
]

const AREA_OPTIONS = [
  { key: 'health',        label: 'Health',          color: '#4caf7d' },
  { key: 'mind',          label: 'Mind',            color: '#9b5ce0' },
  { key: 'career',        label: 'Career & Growth', color: '#c9a84c' },
  { key: 'relationships', label: 'Relationships',   color: '#e05c5c' },
  { key: 'faith',         label: 'Faith',           color: '#5c8fe0' },
  { key: 'purpose',       label: 'Goals',         color: '#e07d3c' },
]

const ICON_OPTIONS = ['🏋️','🧘','📖','💸','📞','🎯','🏃','🥗','💪','🧠','📝','💼','🎨','🎵','🙏','❤️','🌿','⚡','🔥','💡','📚','🛌','🚶','🏊']

export default function HabitsSettingsPage({ onNavigate }) {
  const { user, profile } = useAuth()
  const [customHabits, setCustomHabits] = useState({})
  const [editing, setEditing] = useState(null)
  const [editLabel, setEditLabel] = useState('')
  const [editIcon, setEditIcon] = useState('⭐')
  const [editArea, setEditArea] = useState('health')
  const [saving, setSaving] = useState(false)
  const [showAddHabit, setShowAddHabit] = useState(false)
  const [newLabel, setNewLabel] = useState('')
  const [newIcon, setNewIcon] = useState('⭐')
  const [newArea, setNewArea] = useState('health')

  useEffect(() => {
    if (user) loadCustomHabits()
  }, [user])

  async function loadCustomHabits() {
    const { data } = await supabase
      .from('custom_habits')
      .select('*')
      .eq('user_id', user.id)
    if (data) {
      const map = {}
      data.forEach(h => { map[h.habit_key] = h })
      setCustomHabits(map)
    }
  }

  function startEdit(habit) {
    const custom = customHabits[habit.key]
    setEditing(habit.key)
    setEditLabel(custom?.label || habit.label)
    setEditIcon(custom?.icon || habit.icon)
    setEditArea(custom?.area || habit.area)
  }

  function cancelEdit() {
    setEditing(null)
    setEditLabel('')
    setEditIcon('⭐')
    setEditArea('health')
  }

  async function saveHabit(habitKey) {
    if (!editLabel.trim()) return
    setSaving(true)
    const { data: existing } = await supabase
      .from('custom_habits')
      .select('id')
      .eq('user_id', user.id)
      .eq('habit_key', habitKey)
      .single()

    if (existing) {
      await supabase
        .from('custom_habits')
        .update({ label: editLabel.trim(), icon: editIcon, area: editArea })
        .eq('user_id', user.id)
        .eq('habit_key', habitKey)
    } else {
      await supabase
        .from('custom_habits')
        .insert({ user_id: user.id, habit_key: habitKey, label: editLabel.trim(), icon: editIcon, area: editArea })
    }

    await loadCustomHabits()
    setSaving(false)
    setEditing(null)
  }

  async function resetHabit(habitKey) {
    await supabase.from('custom_habits').delete()
      .eq('user_id', user.id)
      .eq('habit_key', habitKey)
    await loadCustomHabits()
    setEditing(null)
  }

  async function addNewHabit() {
    if (!newLabel.trim()) return
    setSaving(true)
    const habitKey = 'custom_' + Date.now()
    await supabase.from('custom_habits').insert({
      user_id: user.id,
      habit_key: habitKey,
      label: newLabel.trim(),
      icon: newIcon,
      area: newArea,
    })
    setNewLabel('')
    setNewIcon('⭐')
    setNewArea('health')
    setShowAddHabit(false)
    await loadCustomHabits()
    setSaving(false)
  }

  return (
    <div style={{ ...s.page, background: '#111118', color: '#e8e6df' }}>
      <div style={s.ambient} />

      <div style={{ ...s.header, background: 'rgba(6,6,8,0.85)', borderBottom: '1px solid rgba(255,255,255,0.12)' }}>
        <div style={{ ...s.backBtn, background: '#1a1a26', border: '1px solid rgba(255,255,255,0.12)' }} onClick={() => onNavigate('profile')}>←</div>
        <div style={s.headerTitle}>Customize Habits</div>
        <div style={{ width: 36 }} />
      </div>

      <div style={s.main}>
        <div style={{ ...s.intro, color: '#6b6860' }}>
          Tap any habit to rename it, change its icon, or reassign it to a different life area.
        </div>

        {DEFAULT_HABITS.map((habit) => {
          const custom = customHabits[habit.key]
          const displayLabel = custom?.label || habit.label
          const displayIcon = custom?.icon || habit.icon
          const displayArea = custom?.area || habit.area
          const areaColor = AREA_OPTIONS.find(a => a.key === displayArea)?.color || '#c9a84c'
          const isEditing = editing === habit.key

          return (
            <div key={habit.key} style={{ marginBottom: '12px' }}>
              {!isEditing && (
                <div style={{ ...s.habitCard, background: '#1a1a26', border: '1px solid rgba(255,255,255,0.12)' }}
                  onClick={() => startEdit(habit)}>
                  <div style={s.habitLeft}>
                    <div style={s.habitIcon}>{displayIcon}</div>
                    <div>
                      <div style={{ ...s.habitLabel, color: '#e8e6df' }}>{displayLabel}</div>
                      <div style={{ ...s.habitArea, color: areaColor }}>{AREA_OPTIONS.find(a => a.key === displayArea)?.label}</div>
                    </div>
                  </div>
                  <div style={s.habitRight}>
                    <div style={{ ...s.editBtn, color: '#6b6860' }}>Edit →</div>
                  </div>
                </div>
              )}

              {isEditing && (
                <div style={{ ...s.editCard, background: '#1a1a26', border: `1px solid rgba(201,168,76,0.3)` }}>
                  <div style={s.editHeader}>
                    <div style={{ ...s.editTitle, color: '#e8e6df' }}>Editing Habit</div>
                    <div style={{ ...s.cancelBtn, color: '#6b6860' }} onClick={cancelEdit}>Cancel</div>
                  </div>

                  <div style={s.fieldGroup}>
                    <div style={{ ...s.fieldLabel, color: '#6b6860' }}>Habit Name</div>
                    <input
                      style={{ ...s.input, background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', color: '#e8e6df' }}
                      value={editLabel}
                      onChange={e => setEditLabel(e.target.value)}
                      placeholder="e.g. Go for a run"
                      maxLength={40}
                    />
                  </div>

                  <div style={s.fieldGroup}>
                    <div style={{ ...s.fieldLabel, color: '#6b6860' }}>Icon</div>
                    <div style={s.iconGrid}>
                      {ICON_OPTIONS.map(icon => (
                        <div key={icon} style={{ ...s.iconOption, background: editIcon === icon ? 'rgba(201,168,76,0.2)' : 'rgba(255,255,255,0.07)', border: editIcon === icon ? '1px solid rgba(201,168,76,0.5)' : '1px solid rgba(255,255,255,0.12)' }}
                          onClick={() => setEditIcon(icon)}>{icon}</div>
                      ))}
                    </div>
                  </div>

                  <div style={s.fieldGroup}>
                    <div style={{ ...s.fieldLabel, color: '#6b6860' }}>Life Area</div>
                    <div style={s.areaGrid}>
                      {AREA_OPTIONS.map(area => (
                        <div key={area.key} style={{ ...s.areaOption, background: editArea === area.key ? `${area.color}22` : 'rgba(255,255,255,0.07)', border: editArea === area.key ? `1px solid ${area.color}` : '1px solid rgba(255,255,255,0.12)', color: editArea === area.key ? area.color : '#6b6860' }}
                          onClick={() => setEditArea(area.key)}>{area.label}</div>
                      ))}
                    </div>
                  </div>

                  <div style={s.editActions}>
                    {!!custom && (
                      <div style={s.resetBtn} onClick={() => resetHabit(habit.key)}>Reset to Default</div>
                    )}
                    <div style={{ ...s.saveBtn, opacity: saving ? 0.6 : 1 }} onClick={() => saveHabit(habit.key)}>
                      {saving ? 'Saving...' : 'Save Habit'}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )
        })}

        {/* ADD HABIT BUTTON */}
        <>
          {!showAddHabit ? (
            <div style={s.addHabitBtn} onClick={() => setShowAddHabit(true)}>+ Add Habit</div>
          ) : (
            <div style={{ ...s.editCard, background: '#1a1a26', border: '1px solid rgba(201,168,76,0.3)', marginTop: '12px' }}>
              <div style={s.editHeader}>
                <div style={{ ...s.editTitle, color: '#e8e6df' }}>New Habit</div>
                <div style={{ ...s.cancelBtn, color: '#6b6860' }} onClick={() => setShowAddHabit(false)}>Cancel</div>
              </div>
              <div style={s.fieldGroup}>
                <div style={{ ...s.fieldLabel, color: '#6b6860' }}>Habit Name</div>
                <input
                  style={{ ...s.input, background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', color: '#e8e6df' }}
                  value={newLabel}
                  onChange={e => setNewLabel(e.target.value)}
                  placeholder="e.g. Morning walk"
                  maxLength={40}
                />
              </div>
              <div style={s.fieldGroup}>
                <div style={{ ...s.fieldLabel, color: '#6b6860' }}>Icon</div>
                <div style={s.iconGrid}>
                  {ICON_OPTIONS.map(icon => (
                    <div key={icon} style={{ ...s.iconOption, background: newIcon === icon ? 'rgba(201,168,76,0.2)' : 'rgba(255,255,255,0.07)', border: newIcon === icon ? '1px solid rgba(201,168,76,0.5)' : '1px solid rgba(255,255,255,0.12)' }}
                      onClick={() => setNewIcon(icon)}>{icon}</div>
                  ))}
                </div>
              </div>
              <div style={s.fieldGroup}>
                <div style={{ ...s.fieldLabel, color: '#6b6860' }}>Life Area</div>
                <div style={s.areaGrid}>
                  {AREA_OPTIONS.map(area => (
                    <div key={area.key} style={{ ...s.areaOption, background: newArea === area.key ? `${area.color}22` : 'rgba(255,255,255,0.07)', border: newArea === area.key ? `1px solid ${area.color}` : '1px solid rgba(255,255,255,0.12)', color: newArea === area.key ? area.color : '#6b6860' }}
                      onClick={() => setNewArea(area.key)}>{area.label}</div>
                  ))}
                </div>
              </div>
              <div style={{ ...s.saveBtn, opacity: saving ? 0.6 : 1 }} onClick={addNewHabit}>
                {saving ? 'Saving...' : 'Add Habit'}
              </div>
            </div>
          )}
        </>
      </div>
    </div>
  )
}

const s = {
  page: { minHeight: '100vh', fontFamily: "'DM Sans', sans-serif", position: 'relative', overflowX: 'hidden' },
  ambient: { position: 'fixed', top: '-30%', left: '50%', transform: 'translateX(-50%)', width: '800px', height: '800px', background: 'radial-gradient(ellipse, rgba(201,168,76,0.22) 0%, transparent 70%)', pointerEvents: 'none', zIndex: 0 },
  header: { position: 'sticky', top: 0, zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: 'max(18px, env(safe-area-inset-top))', paddingBottom: '18px', paddingLeft: '24px', paddingRight: '24px', backdropFilter: 'blur(16px)' },
  backBtn: { width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '10px', cursor: 'pointer', fontSize: '18px', color: '#c9a84c' },
  headerTitle: { fontFamily: "'Bebas Neue', sans-serif", fontSize: '20px', letterSpacing: '3px', color: '#c9a84c' },
  main: { maxWidth: '420px', margin: '0 auto', padding: '20px 20px 100px', position: 'relative', zIndex: 1 },
  intro: { fontSize: '13px', lineHeight: '1.6', marginBottom: '20px' },
  habitCard: { borderRadius: '14px', padding: '14px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' },
  habitLeft: { display: 'flex', alignItems: 'center', gap: '12px' },
  habitIcon: { fontSize: '22px' },
  habitLabel: { fontSize: '14px', fontWeight: '500' },
  habitArea: { fontSize: '11px', marginTop: '2px', letterSpacing: '0.5px' },
  habitRight: { display: 'flex', alignItems: 'center', gap: '8px' },
  editBtn: { fontSize: '12px' },
  editCard: { borderRadius: '14px', padding: '18px' },
  editHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' },
  editTitle: { fontSize: '14px', fontWeight: '600' },
  cancelBtn: { fontSize: '13px', cursor: 'pointer' },
  fieldGroup: { marginBottom: '16px' },
  fieldLabel: { fontSize: '11px', letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: '8px' },
  input: { width: '100%', borderRadius: '10px', padding: '12px 14px', fontSize: '14px', fontFamily: "'DM Sans', sans-serif", outline: 'none', boxSizing: 'border-box' },
  iconGrid: { display: 'flex', flexWrap: 'wrap', gap: '6px' },
  iconOption: { width: '38px', height: '38px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px', cursor: 'pointer' },
  areaGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' },
  areaOption: { padding: '10px 12px', borderRadius: '10px', fontSize: '12px', textAlign: 'center', cursor: 'pointer', fontWeight: '500' },
  editActions: { display: 'flex', gap: '10px', marginTop: '4px' },
  resetBtn: { flex: 1, padding: '12px', background: 'rgba(255,255,255,0.05)', color: '#6b6860', borderRadius: '10px', textAlign: 'center', cursor: 'pointer', fontSize: '13px' },
  saveBtn: { flex: 2, padding: '12px', background: 'linear-gradient(135deg, #c9a84c, #8b6a1a)', color: '#000', borderRadius: '10px', textAlign: 'center', cursor: 'pointer', fontSize: '14px', fontWeight: '700' },
  addHabitBtn: { marginTop: '16px', padding: '14px', background: 'rgba(201,168,76,0.08)', border: '1px dashed rgba(201,168,76,0.3)', borderRadius: '12px', color: '#c9a84c', fontSize: '14px', fontWeight: '600', textAlign: 'center', cursor: 'pointer' },
  addHabitBtnFree: { marginTop: '16px', padding: '14px', background: 'rgba(255,255,255,0.03)', border: '1px dashed rgba(255,255,255,0.1)', borderRadius: '12px', color: '#6b6860', fontSize: '14px', fontWeight: '600', textAlign: 'center', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' },
  proTag: { fontSize: '10px', fontWeight: '700', color: '#c9a84c', background: 'rgba(201,168,76,0.12)', border: '1px solid rgba(201,168,76,0.2)', padding: '2px 6px', borderRadius: '4px', letterSpacing: '0.5px' },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 200 },
  upsellModal: { position: 'fixed', bottom: 0, left: 0, right: 0, background: '#1a1a26', border: '1px solid rgba(201,168,76,0.25)', borderRadius: '20px 20px 0 0', padding: '28px 24px 48px', zIndex: 201 },
  upsellTitle: { fontFamily: "'Bebas Neue', sans-serif", fontSize: '22px', letterSpacing: '3px', color: '#f0cc7a', marginBottom: '10px' },
  upsellText: { fontSize: '14px', color: '#e8e6df', lineHeight: '1.6', marginBottom: '8px' },
  upsellPrice: { fontSize: '13px', color: '#c9a84c', fontWeight: '500', marginBottom: '20px' },
  upsellBtn: { padding: '14px', background: 'linear-gradient(135deg, #c9a84c, #8b6a1a)', color: '#000', borderRadius: '12px', textAlign: 'center', cursor: 'pointer', fontSize: '15px', fontWeight: '700', marginBottom: '10px' },
  upsellDismiss: { textAlign: 'center', fontSize: '13px', color: '#6b6860', cursor: 'pointer', padding: '8px' },
}
